/**
 * Vehicle Selector — cascading Make → Model → Year → Trim dropdowns
 * with a second "Søk etter dekk" tab for tyre size search.
 *
 * Custom element <vehicle-selector> that:
 * - Tab 1: Fetches makes, cascades model/year/trim, navigates to /search
 * - Tab 2: Fetches tyre facets (width/profile/diameter), navigates to tyre search page
 * - Persists vehicle selection to localStorage via vehicle-store
 */

import { getVehicle, saveVehicle, clearVehicle } from './vehicle-store.js';
// Force refresh 23-09-2026
const API_BASE_FALLBACK = 'https://admin.sbfwheels.no';

class VehicleSelectorElement extends HTMLElement {
  connectedCallback() {
    this._api = this.dataset.apiBase || API_BASE_FALLBACK;
    this._context = this.dataset.context || 'search';

    // Vehicle selector refs (Tab 1)
    this._makeSelect = this.querySelector('[data-role="make"]');
    this._modelSelect = this.querySelector('[data-role="model"]');
    this._yearSelect = this.querySelector('[data-role="year"]');
    this._trimSelect = this.querySelector('[data-role="trim"]');
    this._searchBtn = this.querySelector('[data-role="search"]');
    this._clearBtn = this.querySelector('[data-role="clear"]');

    // Tyre selector refs (Tab 2)
    this._tyreWidth = this.querySelector('[data-role="tyre-width"]');
    this._tyreProfile = this.querySelector('[data-role="tyre-profile"]');
    this._tyreDiameter = this.querySelector('[data-role="tyre-diameter"]');
    this._tyreSearchBtn = this.querySelector('[data-role="tyre-search"]');
    this._tyreSearchUrl = this.dataset.tyreSearchUrl || '/collections/dekk';
    this._tyreFacetsLoaded = false;

    // Tab refs
    this._tabs = this.querySelectorAll('[data-tab]');
    this._panels = this.querySelectorAll('[data-panel]');

    // Internal state for building the saved object
    this._makes = [];
    this._models = [];
    this._years = [];
    this._trims = [];

    // Vehicle selector events
    if (this._makeSelect) this._makeSelect.addEventListener('change', () => this._onMakeChange());
    if (this._modelSelect) this._modelSelect.addEventListener('change', () => this._onModelChange());
    if (this._yearSelect) this._yearSelect.addEventListener('change', () => this._onYearChange());
    if (this._trimSelect) this._trimSelect.addEventListener('change', () => this._onTrimChange());
    if (this._searchBtn) this._searchBtn.addEventListener('click', () => this._onSearch());
    if (this._clearBtn) this._clearBtn.addEventListener('click', () => this._onClear());

    // Tab switching events
    this._tabs.forEach(tab => tab.addEventListener('click', () => this._switchTab(tab)));

    // Regnr refs (inline in vehicle panel)
    this._regnrInput = this.querySelector('[data-role="regnr-input"]');
    this._regnrSearchBtn = this.querySelector('[data-role="regnr-search"]');
    this._regnrStatus = this.querySelector('[data-role="regnr-status"]');

    // Regnr events
    if (this._regnrInput) {
      this._regnrInput.addEventListener('input', () => {
        const val = this._regnrInput.value.replace(/[\s-]/g, '');
        if (this._regnrSearchBtn) {
          this._regnrSearchBtn.disabled = val.length < 4;
          if (val.length >= 4) this._regnrSearchBtn.dataset.revealed = '';
          else delete this._regnrSearchBtn.dataset.revealed;
        }
        if (this._regnrStatus) this._regnrStatus.textContent = '';
      });
      this._regnrInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !this._regnrSearchBtn?.disabled) this._onRegnrSearch();
      });
    }
    if (this._regnrSearchBtn) this._regnrSearchBtn.addEventListener('click', () => this._onRegnrSearch());

    // Tyre selector events (cascading like vehicle tab)
    if (this._tyreWidth) this._tyreWidth.addEventListener('change', () => this._onTyreWidthChange());
    if (this._tyreProfile) this._tyreProfile.addEventListener('change', () => this._onTyreProfileChange());
    if (this._tyreDiameter) this._tyreDiameter.addEventListener('change', () => this._onTyreDiameterChange());
    if (this._tyreSearchBtn) this._tyreSearchBtn.addEventListener('click', () => this._onTyreSearch());

    this._loadMakes();
    this._loadTyreFacets();
  }

  // ── Tab switching ─────────────────────────────────────────────────

  _switchTab(activeTab) {
    this._tabs.forEach(t => t.classList.toggle('vehicle-selector__tab--active', t === activeTab));
    this._panels.forEach(p => {
      p.hidden = p.dataset.panel !== activeTab.dataset.tab;
    });
    if (activeTab.dataset.tab === 'tyre') this._loadTyreFacets();
  }

  // ── Regnr lookup ─────────────────────────────────────────────────

  async _onRegnrSearch() {
    const plate = (this._regnrInput?.value || '').replace(/[\s-]/g, '').toUpperCase();
    if (plate.length < 4) return;

    if (this._regnrSearchBtn) this._regnrSearchBtn.disabled = true;
    if (this._regnrStatus) this._regnrStatus.textContent = 'Søker...';
    if (this._regnrStatus) this._regnrStatus.style.color = '#aaa';

    try {
      const res = await fetch(`${this._api}/api/vehicles/regnr/${encodeURIComponent(plate)}`);
      const json = await res.json();

      if (!res.ok || !json.success) {
        if (this._regnrStatus) {
          this._regnrStatus.textContent = (json.error?.message || json.error) || 'Ikke funnet';
          this._regnrStatus.style.color = '#e57373';
        }
        if (this._regnrSearchBtn) this._regnrSearchBtn.disabled = false;
        return;
      }

      const meta = json.meta;
      const vehicle = {
        makeSlug: meta.make_slug,
        makeName: meta.make,
        modelSlug: meta.model_slug,
        modelName: meta.model,
        year: meta.year ? String(meta.year) : '',
        trimId: meta.trim_id ? String(meta.trim_id) : `regnr:${plate}`,
        trimName: meta.model,
        regnr: plate,
        regnrSource: 'vegvesen',
      };

      // Save specs from the response
      saveVehicle(vehicle);

      // Clear temporary lookup status after success.
      if (this._regnrStatus) {
        this._regnrStatus.textContent = '';
        this._regnrStatus.style.color = '';
      }

      // In Studio context we keep the user on the Studio page. saveVehicle()
      // already dispatches vehicle:changed so the Studio can unlock upload.
      if (this._context === 'studio') return;

      // Default storefront behaviour: navigate to search page with regnr param
      window.location.href = `/search?regnr=${encodeURIComponent(plate)}`;
    } catch (e) {
      console.error('Regnr lookup failed', e);
      if (this._regnrStatus) {
        this._regnrStatus.textContent = 'Noe gikk galt';
        this._regnrStatus.style.color = '#e57373';
      }
      if (this._regnrSearchBtn) this._regnrSearchBtn.disabled = false;
    }
  }

  // ── Tyre facet loading (cascading) ───────────────────────────────

  async _loadTyreFacets() {
    if (this._tyreFacetsLoaded) return;
    try {
      const res = await fetch(`${this._api}/api/v2/tires?limit=1`);
      if (!res.ok) throw new Error(`API ${res.status}`);
      const json = await res.json();
      this._populateTyreSelect(this._tyreWidth, json.facets.width, 'Bredde');
      if (this._tyreWidth) this._tyreWidth.disabled = false;
      this._tyreFacetsLoaded = true;
    } catch (e) {
      console.error('Vehicle selector: failed to load tyre facets', e);
    }
  }

  _populateTyreSelect(select, facetItems, placeholder) {
    if (!select || !facetItems) return;
    const opts = [`<option value="">${placeholder}</option>`];
    for (const item of facetItems) {
      opts.push(`<option value="${item.value}">${item.value}</option>`);
    }
    select.innerHTML = opts.join('');
  }

  _tyreReveal(el, show) {
    if (!el) return;
    if (show) { el.disabled = false; el.dataset.revealed = ''; }
    else { el.disabled = true; el.value = ''; delete el.dataset.revealed; }
  }

  async _onTyreWidthChange() {
    this._tyreReveal(this._tyreProfile, false);
    this._tyreReveal(this._tyreDiameter, false);
    this._tyreReveal(this._tyreSearchBtn, false);
    if (!this._tyreWidth?.value) return;
    try {
      const res = await fetch(`${this._api}/api/v2/tires?limit=1&width=${this._tyreWidth.value}`);
      if (!res.ok) throw new Error(`API ${res.status}`);
      const json = await res.json();
      this._populateTyreSelect(this._tyreProfile, json.facets.profile, 'Profil');
      this._tyreReveal(this._tyreProfile, true);
    } catch (e) {
      console.error('Vehicle selector: failed to load profiles', e);
    }
  }

  async _onTyreProfileChange() {
    this._tyreReveal(this._tyreDiameter, false);
    this._tyreReveal(this._tyreSearchBtn, false);
    if (!this._tyreProfile?.value) return;
    try {
      const res = await fetch(`${this._api}/api/v2/tires?limit=1&width=${this._tyreWidth.value}&profile=${this._tyreProfile.value}`);
      if (!res.ok) throw new Error(`API ${res.status}`);
      const json = await res.json();
      this._populateTyreSelect(this._tyreDiameter, json.facets.diameter, 'Diameter');
      this._tyreReveal(this._tyreDiameter, true);
    } catch (e) {
      console.error('Vehicle selector: failed to load diameters', e);
    }
  }

  _onTyreDiameterChange() {
    if (this._tyreSearchBtn) {
      if (this._tyreDiameter?.value) {
        this._tyreSearchBtn.disabled = false;
        this._tyreSearchBtn.dataset.revealed = '';
      } else {
        this._tyreSearchBtn.disabled = true;
        delete this._tyreSearchBtn.dataset.revealed;
      }
    }
  }

  _onTyreSearch() {
    const params = new URLSearchParams();
    if (this._tyreWidth?.value) params.set('width', this._tyreWidth.value);
    if (this._tyreProfile?.value) params.set('profile', this._tyreProfile.value);
    if (this._tyreDiameter?.value) params.set('diameter', this._tyreDiameter.value);
    const qs = params.toString();
    window.location.href = qs ? `${this._tyreSearchUrl}?${qs}` : this._tyreSearchUrl;
  }

  // ── Vehicle data fetching ─────────────────────────────────────────

  async _fetchJson(path) {
    const res = await fetch(`${this._api}${path}`);
    if (!res.ok) throw new Error(`API ${res.status}`);
    const json = await res.json();
    return json.data ?? json;
  }

  async _loadMakes() {
    try {
      this._makes = await this._fetchJson('/api/vehicles/makes');
      this._populateSelect(this._makeSelect, this._makes, 'Merke', (m) => ({
        value: m.slug,
        label: m.name,
      }));
      this._makeSelect.disabled = false;
      this._restoreFromStorage();
    } catch (e) {
      console.error('Vehicle selector: failed to load makes', e);
    }
  }

  async _loadModels(makeSlug) {
    this._resetSelect(this._modelSelect, 'Modell');
    this._resetSelect(this._yearSelect, '\u00c5r');
    this._resetSelect(this._trimSelect, 'Type');
    this._searchBtn.disabled = true;
    try {
      this._models = await this._fetchJson(`/api/vehicles/makes/${makeSlug}`);
      this._populateSelect(this._modelSelect, this._models, 'Modell', (m) => {
        return { value: m.slug, label: m.ModelName || m.modelName || m.name || '' };
      });
      this._modelSelect.disabled = false;
      this._updateMobileSteps();
    } catch (e) {
      console.error('Vehicle selector: failed to load models', e);
    }
  }

  async _loadYears(makeSlug, modelSlug) {
    this._resetSelect(this._yearSelect, '\u00c5r');
    this._resetSelect(this._trimSelect, 'Type');
    this._searchBtn.disabled = true;
    try {
      this._years = await this._fetchJson(
        `/api/vehicles/makes/${makeSlug}/models/${modelSlug}`
      );
      this._populateSelect(this._yearSelect, this._years, '\u00c5r', (y) => ({
        value: y, label: y,
      }));
      this._yearSelect.disabled = false;
      this._updateMobileSteps();
    } catch (e) {
      console.error('Vehicle selector: failed to load years', e);
    }
  }

  async _loadTrims(makeSlug, modelSlug, year) {
    this._resetSelect(this._trimSelect, 'Type');
    this._searchBtn.disabled = true;
    try {
      this._trims = await this._fetchJson(
        `/api/vehicles/makes/${makeSlug}/models/${modelSlug}/years/${year}`
      );
      this._populateSelect(this._trimSelect, this._trims, 'Type', (t) => {
        const name = t.CarName || t.carName || t.name || '';
        return { value: String(t.CarID || t.carId || t.id || ''), label: name };
      });
      this._trimSelect.disabled = false;
      this._updateMobileSteps();
    } catch (e) {
      console.error('Vehicle selector: failed to load trims', e);
    }
  }

  // ── Select helpers ───────────────────────────────────────────────

  _populateSelect(select, items, placeholder, mapFn) {
    const options = [`<option value="">${placeholder}</option>`];
    for (const item of items) {
      const { value, label } = mapFn(item);
      options.push(`<option value="${value}">${this._escHtml(label)}</option>`);
    }
    select.innerHTML = options.join('');
  }

  _resetSelect(select, placeholder) {
    select.innerHTML = `<option value="">${placeholder}</option>`;
    select.disabled = true;
  }

  _escHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ── Vehicle event handlers ────────────────────────────────────────

  _onMakeChange() {
    const slug = this._makeSelect.value;
    if (slug) {
      this._loadModels(slug);
      // Clear regnr when switching to make/model flow
      if (this._regnrInput) this._regnrInput.value = '';
      if (this._regnrSearchBtn) {
        this._regnrSearchBtn.disabled = true;
        delete this._regnrSearchBtn.dataset.revealed;
      }
      if (this._regnrStatus) this._regnrStatus.textContent = '';
    } else {
      this._resetSelect(this._modelSelect, 'Modell');
      this._resetSelect(this._yearSelect, '\u00c5r');
      this._resetSelect(this._trimSelect, 'Type');
      this._searchBtn.disabled = true;
    }
    this._updateMobileSteps();
  }

  _onModelChange() {
    const makeSlug = this._makeSelect.value;
    const modelSlug = this._modelSelect.value;
    if (makeSlug && modelSlug) {
      this._loadYears(makeSlug, modelSlug);
    } else {
      this._resetSelect(this._yearSelect, '\u00c5r');
      this._resetSelect(this._trimSelect, 'Type');
      this._searchBtn.disabled = true;
    }
    this._updateMobileSteps();
  }

  _onYearChange() {
    const makeSlug = this._makeSelect.value;
    const modelSlug = this._modelSelect.value;
    const year = this._yearSelect.value;
    if (makeSlug && modelSlug && year) {
      this._loadTrims(makeSlug, modelSlug, year);
    } else {
      this._resetSelect(this._trimSelect, 'Type');
      this._searchBtn.disabled = true;
    }
    this._updateMobileSteps();
  }

  _onTrimChange() {
    if (this._searchBtn) {
      this._searchBtn.disabled = !this._trimSelect.value;
    }

    this._updateMobileSteps();

    // Studio: Type is the final step.
    // Save/show the selected vehicle immediately on both desktop and mobile.
    if (
      this._context === 'studio' &&
      this._trimSelect.value
    ) {
      window.setTimeout(() => this._onSearch(), 60);
    }
  }

  _onSearch() {
    const makeSlug = this._makeSelect.value;
    const modelSlug = this._modelSelect.value;
    const trimId = this._trimSelect.value;
    if (!makeSlug || !modelSlug || !trimId) return;

    // Find the selected items for display names
    const make = this._makes.find((m) => m.slug === makeSlug);
    const model = this._models.find((m) => m.slug === modelSlug);
    const year = this._yearSelect.value;
    const trim = this._trims.find(
      (t) => String(t.CarID || t.carId || t.id || '') === trimId
    );

    const vehicle = {
      makeSlug,
      makeName: make?.name || makeSlug,
      modelSlug,
      modelName: (model?.ModelName || model?.modelName || model?.name || modelSlug),
      year,
      trimId,
      trimName: trim?.CarName || trim?.carName || trim?.name || trimId,
    };

    // Fetch specs in the background and save (don't block navigation)
    saveVehicle(vehicle);

    // Studio context stores the vehicle but must not leave the Studio page.
    if (this._context === 'studio') return;

    // Default storefront behaviour: navigate to search page
    const params = new URLSearchParams({ make: makeSlug, model: modelSlug, trim: trimId });
    window.location.href = `/search?${params}`;
  }

  _onClear() {
    clearVehicle();
    this._makeSelect.value = '';
    this._resetSelect(this._modelSelect, 'Modell');
    this._resetSelect(this._yearSelect, '\u00c5r');
    this._resetSelect(this._trimSelect, 'Type');
    this._searchBtn.disabled = true;
    // Reset regnr
    if (this._regnrInput) this._regnrInput.value = '';
    if (this._regnrSearchBtn) {
      this._regnrSearchBtn.disabled = true;
      delete this._regnrSearchBtn.dataset.revealed;
    }
    if (this._regnrStatus) this._regnrStatus.textContent = '';
    this._updateMobileSteps();
  }

  // ── Restore saved vehicle ────────────────────────────────────────

  async _restoreFromStorage() {
    const saved = getVehicle();
    if (!saved?.makeSlug) return;

    // Skip animations when restoring saved state
    this.dataset.noTransition = '';

    // Set make
    this._makeSelect.value = saved.makeSlug;
    if (!this._makeSelect.value) {
      // Slug not found in options (e.g. regnr vehicle not in CSV) — reset to placeholder
      this._makeSelect.selectedIndex = 0;
      delete this.dataset.noTransition;
      return;
    }

    // Load and set model
    await this._loadModels(saved.makeSlug);
    if (saved.modelSlug) {
      this._modelSelect.value = saved.modelSlug;
      if (this._modelSelect.value) {
        await this._loadYears(saved.makeSlug, saved.modelSlug);
        if (saved.year) {
          this._yearSelect.value = saved.year;
          if (this._yearSelect.value) {
            await this._loadTrims(saved.makeSlug, saved.modelSlug, saved.year);
            if (saved.trimId) {
              this._trimSelect.value = saved.trimId;
              this._searchBtn.disabled = !this._trimSelect.value;
              this._updateMobileSteps();
            }
          }
        }
      }
    }

    // Re-enable transitions
    void this.offsetHeight;
    delete this.dataset.noTransition;
  }

  // ── Mobile progressive reveal ───────────────────────────────────

  _updateMobileSteps() {
    const hasMake = !!this._makeSelect?.value;
    const hasModel = !!this._modelSelect?.value;
    const hasYear = !!this._yearSelect?.value;
    const hasTrim = !!this._trimSelect?.value;

    // Expose the current cascade state to the Studio mobile UI.
    if (hasMake) this.dataset.makeSelected = '';
    else delete this.dataset.makeSelected;

    if (hasModel) this.dataset.modelSelected = '';
    else delete this.dataset.modelSelected;

    if (hasYear) this.dataset.yearSelected = '';
    else delete this.dataset.yearSelected;

    if (hasTrim) this.dataset.trimSelected = '';
    else delete this.dataset.trimSelected;

    const reveal = (el, show) => {
      if (!el) return;
      if (show) el.dataset.revealed = '';
      else delete el.dataset.revealed;
    };

    reveal(this._modelSelect, !this._modelSelect.disabled);
    reveal(this._yearSelect, !this._yearSelect.disabled);
    reveal(this._trimSelect, !this._trimSelect.disabled);
    reveal(this._searchBtn, !this._searchBtn.disabled);
    reveal(this._clearBtn, hasMake);
  }
}

if (!customElements.get('vehicle-selector')) {
  customElements.define('vehicle-selector', VehicleSelectorElement);
}
